import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { openReport, buildReportName } from "../lib/reportViewerBus";
import DateInput from "./DateInput";
import AvocatAutocomplete from "./AvocatAutocomplete";
import VilleAutocomplete from "./VilleAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modale Partie Adverse — parité VB.NET `FrmInfoPadv` + look & feel aligné
 * sur la modale "Gestion des Liens" (LiensTab.jsx).
 * ---------------------------------------------------------------------------
 * Layout global :
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │  [Formulaire d'édition — 2 groupboxes : PADV + Avocat + Btns]    │
 *   │                                                                    │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │  [Liste tabulaire des parties adverses du dossier (compact)]      │
 *   │  ← clic sur une ligne charge la row dans le formulaire du haut    │
 *   └──────────────────────────────────────────────────────────────────┘
 * ---------------------------------------------------------------------------
 * Endpoints REST utilisés :
 *   GET    /api/reference/liens-padv       — dropdown Lien
 *   GET    /api/reference/provinces        — dropdown Province (PADV + Avocat)
 *   GET    /api/reference/avocats          — dropdown Code avocat (auto-fill)
 *   GET    /api/daj/{pk}/parties-adverses  — liste
 *   POST   /api/daj/{pk}/parties-adverses  — création
 *   PUT    /api/parties-adverses/{rowid}   — mise à jour
 *   DELETE /api/parties-adverses/{rowid}   — suppression
 * ---------------------------------------------------------------------------
 */

const EMPTY_PADV = {
  row_id: "",
  adpalien: "",
  adpanom: "",
  adpaprenom: "",
  adpadatenais: "",
  adpasexe: 1,
  adpaadr: "",
  adpaville: "",
  adpacodpos: "",
  adpaprov: "QC",
  adpatel_1: "",
  adpatel_2: "",
  ext_padv: "",
  courriel_padv: "",
  adpanoclient: "",
  adpanodoss: "",
  adpavocode: "",
  adpavonom: "",
  adpavopren: "",
  adpavoadr: "",
  adpavoville: "",
  adpavoprov: "QC",
  adpavocp: "",
  adpavotel: "",
  ext_avo: "",
  courriel_avo: "",
};

const isValidEmail = (s) => !s || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

// Formatte téléphone au format legacy (999) 999-9999
const formatTelephone = (raw) => {
  const d = (raw || "").replace(/[^\d]/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

// Formatte code postal AAA AAA
const formatCodePostal = (raw) => {
  const s = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  if (s.length <= 3) return s;
  return `${s.slice(0, 3)} ${s.slice(3)}`;
};

// Capitalise chaque mot (parité VB.NET SetNom)
const formatNomPropre = (s) => {
  if (!s) return "";
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
};

const PartieAdverseModal = ({ isOpen, onClose, pkDaj, showPopup, formData, requerantNom, requerantPrenom }) => {
  const [liste, setListe] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState("");
  const [form, setForm] = useState({ ...EMPTY_PADV });
  const [liensPadv, setLiensPadv] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [avocats, setAvocats] = useState([]);
  const [isEditing, setIsEditing] = useState(false); // true après clic Nouveau/Modifier
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const canEdit = !!pkDaj;

  // ---------- Chargement dropdowns une fois à l'ouverture ----------
  useEffect(() => {
    if (!isOpen) return;
    axios.get(`${API}/reference/liens-padv`).then(r => setLiensPadv(r.data || [])).catch(() => {});
    axios.get(`${API}/reference/provinces`).then(r => setProvinces(r.data || [])).catch(() => {});
    axios.get(`${API}/reference/avocats`).then(r => setAvocats(r.data || [])).catch(() => {});
  }, [isOpen]);

  // ---------- Chargement liste PADV du dossier ----------
  const loadListe = useCallback(async () => {
    if (!pkDaj) { setListe([]); return; }
    try {
      const { data } = await axios.get(`${API}/daj/${pkDaj}/parties-adverses`);
      setListe(data || []);
    } catch (e) {
      console.error("[PartieAdverseModal] Erreur chargement liste:", e?.message);
      setListe([]);
    }
  }, [pkDaj]);

  useEffect(() => { if (isOpen) loadListe(); }, [isOpen, loadListe]);

  // ---------- Reset état à la fermeture ----------
  useEffect(() => {
    if (!isOpen) {
      setSelectedRowId("");
      setForm({ ...EMPTY_PADV });
      setIsEditing(false);
      setIsDirty(false);
    }
  }, [isOpen]);

  // ---------- Sélection d'une ligne dans la liste ----------
  const handleSelectRow = (padv) => {
    if (isDirty) {
      if (!window.confirm("Vous avez effectué des changements... Continuer sans enregistrer ?")) return;
    }
    setSelectedRowId(padv.RowId);
    setForm({
      row_id: padv.RowId || "",
      adpalien: padv.adpalien || "",
      adpanom: padv.adpanom || "",
      adpaprenom: padv.adpaprenom || "",
      adpadatenais: (padv.adpadatenais || "").substring(0, 10),
      adpasexe: padv.adpasexe ?? 1,
      adpaadr: padv.adpaadr || "",
      adpaville: padv.adpaville || "",
      adpacodpos: padv.adpacodpos || "",
      adpaprov: padv.adpaprov || "QC",
      adpatel_1: padv.adpatel_1 || "",
      adpatel_2: padv.adpatel_2 || "",
      ext_padv: padv.ext_padv || "",
      courriel_padv: padv.courriel_padv || "",
      adpanoclient: padv.adpanoclient || "",
      adpanodoss: padv.adpanodoss || "",
      adpavocode: padv.adpavocode || "",
      adpavonom: padv.adpavonom || "",
      adpavopren: padv.adpavopren || "",
      adpavoadr: padv.adpavoadr || "",
      adpavoville: padv.adpavoville || "",
      adpavoprov: padv.adpavoprov || "QC",
      adpavocp: padv.adpavocp || "",
      adpavotel: padv.adpavotel || "",
      ext_avo: padv.ext_avo || "",
      courriel_avo: padv.courriel_avo || "",
    });
    setIsEditing(false);
    setIsDirty(false);
  };

  // ---------- Update champ formulaire ----------
  const setField = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };

  // ---------- Copier adresse du requérant vers PADV ----------
  const copyAdresseRequerant = () => {
    if (!formData) return;
    setForm(prev => ({
      ...prev,
      adpaadr: formData.adresse || "",
      adpaville: formData.ville || "",
      adpaprov: formData.province || "QC",
      adpacodpos: formData.code_postal || "",
      adpatel_1: formData.telephone_residence || "",
      adpatel_2: formData.telephone_bureau || "",
    }));
    setIsDirty(true);
  };

  // ---------- Auto-fill avocat depuis code OU sélection dans dropdown ----------
  // Parité DecisionTab.jsx (comportement "Confié à" / "Signature de l'avocat") :
  // quand l'avocat est choisi (par code OU nom via autocomplete), on rapatrie
  // toutes ses coordonnées legacy (rue, ville, province, cp, tel, courriel).
  const applyAvocatSelection = (avocat) => {
    if (!avocat) return;
    const label = avocat.label
      || [avocat.nom, avocat.prenom].filter(Boolean).join(", ");
    setForm(prev => ({
      ...prev,
      adpavocode: (avocat.code || "").trim().toUpperCase(),
      adpavonom: (avocat.nom || "").trim(),
      adpavopren: (avocat.prenom || "").trim(),
      // Coordonnées legacy avocats DDL : rue, ville, province, codepostal, tel, internet
      adpavoadr: (avocat.rue || avocat.adresse || "").trim(),
      adpavoville: (avocat.ville || "").trim(),
      adpavoprov: (avocat.province || prev.adpavoprov || "QC").trim(),
      adpavocp: (avocat.codepostal || avocat.code_postal || "").trim(),
      adpavotel: (avocat.tel || avocat.telephone || "").trim(),
      courriel_avo: (avocat.internet || avocat.courriel || "").trim(),
      ext_avo: (avocat.ext || avocat.extension || prev.ext_avo || "").trim(),
      // Label reconstruit pour l'autocomplete
      _avocatLabel: label,
    }));
    setIsDirty(true);
  };

  const handleAvocatCodeChange = (rawCode) => {
    const code = (rawCode || "").trim().toUpperCase();
    setField("adpavocode", code);
    const av = avocats.find(a => (a.code || "").trim().toUpperCase() === code);
    if (av) applyAvocatSelection(av);
  };

  // ---------- Boutons ----------
  const handleNew = () => {
    if (isDirty && !window.confirm("Vous avez effectué des changements... Continuer sans enregistrer ?")) return;
    setSelectedRowId("");
    setForm({ ...EMPTY_PADV });
    setIsEditing(true);
    setIsDirty(false);
  };

  const handleModif = () => {
    if (!form.row_id) {
      showPopup && showPopup("Sélectionnez d'abord une partie adverse dans la liste.", "info", "Modifier");
      return;
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!(form.adpanom || "").trim() || !(form.adpaprenom || "").trim()) {
      showPopup && showPopup("L'enregistrement doit contenir au minimum\nle nom et le prénom.", "warning", "Partie adverse");
      return;
    }
    if (form.courriel_padv && !isValidEmail(form.courriel_padv)) {
      showPopup && showPopup("L'adresse courriel du partie adverse est invalide.", "warning", "Validation");
      return;
    }
    if (form.courriel_avo && !isValidEmail(form.courriel_avo)) {
      showPopup && showPopup("L'adresse courriel de l'avocat adverse est invalide.", "warning", "Validation");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.row_id) {
        await axios.put(`${API}/parties-adverses/${form.row_id}`, payload);
      } else {
        const { data } = await axios.post(`${API}/daj/${pkDaj}/parties-adverses`, payload);
        setForm(prev => ({ ...prev, row_id: data.RowId, adpanoclient: data.adpanoclient }));
        setSelectedRowId(data.RowId);
      }
      setIsDirty(false);
      setIsEditing(false);
      await loadListe();
      showPopup && showPopup("Partie adverse enregistrée avec succès.", "success", "Partie adverse");
    } catch (e) {
      showPopup && showPopup(`Erreur d'enregistrement :\n${e.response?.data?.detail || e.message}`, "error", "Partie adverse");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!form.row_id) {
      showPopup && showPopup("Sélectionnez d'abord une partie adverse dans la liste.", "info", "Supprimer");
      return;
    }
    const label = `${form.adpanom || ""}, ${form.adpaprenom || ""}`.trim();
    if (!window.confirm(`Vous allez supprimer la partie adverse suivante : ${label}. Voulez-vous continuer ?`)) return;
    try {
      await axios.delete(`${API}/parties-adverses/${form.row_id}`);
      setSelectedRowId("");
      setForm({ ...EMPTY_PADV });
      setIsEditing(false);
      setIsDirty(false);
      await loadListe();
      showPopup && showPopup("Partie adverse supprimée.", "success", "Partie adverse");
    } catch (e) {
      showPopup && showPopup(`Erreur de suppression :\n${e.response?.data?.detail || e.message}`, "error", "Partie adverse");
    }
  };

  const handleCloseModal = () => {
    if (isDirty && !window.confirm("Vous avez effectué des changements... Fermer sans enregistrer ?")) return;
    onClose && onClose();
  };

  // Listes triées + injection legacy fallback si valeur inconnue
  const liensOptions = useMemo(() => {
    const known = new Set(liensPadv.map(l => (l.description || "").trim().toLowerCase()));
    const opts = liensPadv.map(l => ({ value: l.description, label: l.description }));
    const current = (form.adpalien || "").trim();
    if (current && !known.has(current.toLowerCase())) {
      opts.unshift({ value: current, label: `${current} (legacy)`, legacy: true });
    }
    return opts;
  }, [liensPadv, form.adpalien]);

  const provOptions = provinces.map(p => p.value || p.code || p.description).filter(Boolean);

  if (!isOpen) return null;
  const disabled = !isEditing;

  // Formatage lisible de l'identifiant du dossier :
  //   `NR-NB-noseqdaj`  (ex : 90-01-225015001-01)
  // Priorité : décomposition d'un pk_daj complet (16 chars). Sinon on affiche
  // le no_seq_daj tel quel.
  const dossierLabel = (() => {
    const s = (pkDaj || "").trim();
    if (!s) return "(aucun dossier)";
    if (s.length >= 16) return `${s.slice(0, 2)}-${s.slice(2, 4)}-${s.slice(4)}`;
    return s;
  })();
  const requerantLabel = [
    (requerantNom || "").trim(),
    (requerantPrenom || "").trim(),
  ].filter(Boolean).join(", ");

  return (
    <div className="popup-overlay" data-testid="modal-partie-adverse">
      <div
        className="gestion-liens-modal"
        style={{ maxWidth: 820, marginTop: 0 }}
      >
        <div
          className="popup-header"
          style={{ background: '#0033A0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff', padding: '8px 12px' }}
        >
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0 }}>
            <span className="popup-title" style={{ fontWeight: "bold", fontSize: 13 }}>PARTIE ADVERSE</span>
            <span
              data-testid="padv-header-dossier"
              style={{
                fontSize: 11,
                fontWeight: "normal",
                opacity: 0.9,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={`Dossier ${dossierLabel}${requerantLabel ? ` — Requérant : ${requerantLabel}` : ""}`}
            >
              Dossier {dossierLabel}
              {requerantLabel && <> — Requérant : <strong>{requerantLabel}</strong></>}
              {liste.length > 0 && (
                <span style={{ marginLeft: 10, opacity: 0.8 }}>· {liste.length} partie(s) adverse(s)</span>
              )}
            </span>
          </div>
          <button className="win-modal-close" onClick={handleCloseModal} data-testid="btn-padv-close-x">×</button>
        </div>
        <div className="gestion-liens-content" style={{ padding: 12 }}>
          {!canEdit && (
            <div style={{ padding: 8, background: "#fff9c4", border: "1px solid #f9a825", marginBottom: 8, fontSize: 12 }}>
              ⚠ Aucun dossier ouvert. Ouvrez ou créez un dossier avant d&apos;accéder aux parties adverses.
            </div>
          )}

          {/* ============ En-tête : Type de lien + boutons d'action (parité Liens) ============ */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <label className="win-field-label" style={{ fontSize: 11, margin: 0, whiteSpace: "nowrap" }}>Type de lien</label>
            <select className="win-select" style={{ fontSize: 13, padding: '4px 6px', flex: "0 1 220px" }} value={form.adpalien} onChange={e => setField("adpalien", e.target.value)} disabled={disabled} data-testid="padv-cbo-lien">
              <option value="">-- Sélectionner --</option>
              {liensOptions.map(o => <option key={o.value} value={o.value} style={o.legacy ? { fontStyle: "italic", color: "#8b6914" } : {}}>{o.label}</option>)}
            </select>
            <div style={{ flex: 1 }} />
            <button type="button" className="win-btn" onClick={handleNew} disabled={!canEdit} data-testid="btn-padv-new">Nouveau</button>
            <button type="button" className="win-btn" onClick={handleModif} disabled={!canEdit || !form.row_id || isEditing} data-testid="btn-padv-modif">Modifier</button>
            <button type="button" className="win-btn primary" onClick={handleSave} disabled={!canEdit || !isEditing || saving} data-testid="btn-padv-save">{saving ? "..." : "Enregistrer"}</button>
            <button type="button" className="win-btn" onClick={handleDelete} disabled={!canEdit || !form.row_id} data-testid="btn-padv-delete">Supprimer</button>
          </div>

          {/* ============ 2 colonnes côte à côte : PADV | AVOCAT (parité layout Liens) ============ */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>

            {/* ---- Colonne gauche : PARTIE ADVERSE (identité + coordonnées) ---- */}
            <fieldset className="win-groupbox compact" style={{ margin: 0, padding: 8 }}>
              <legend style={{ fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                IDENTITÉ ET COORDONNÉES
                <button
                  className="win-btn"
                  type="button"
                  title="Copier l'adresse du requérant"
                  data-testid="btn-padv-copier-adresse"
                  disabled={disabled}
                  style={{ padding: '2px 5px', lineHeight: 1, minWidth: 'unset' }}
                  onClick={copyAdresseRequerant}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>
                </button>
              </legend>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Nom *</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpanom} onChange={e => setField("adpanom", e.target.value)} onBlur={e => setField("adpanom", formatNomPropre(e.target.value))} maxLength={40} disabled={disabled} data-testid="padv-nom" />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Prénom *</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpaprenom} onChange={e => setField("adpaprenom", e.target.value)} onBlur={e => setField("adpaprenom", formatNomPropre(e.target.value))} maxLength={40} disabled={disabled} data-testid="padv-prenom" />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", gap: 6, marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Date de naissance</label>
                  <DateInput value={form.adpadatenais} onChange={v => setField("adpadatenais", v)} testId="padv-datenais" disabled={disabled} style={{ fontSize: 13, padding: '3px 6px' }} />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Sexe</label>
                  <div className="win-radio-group" style={{ paddingTop: 4 }}>
                    <label style={{ fontSize: 12 }}>
                      <input type="radio" name="padv-sexe" checked={form.adpasexe === 0} onChange={() => setField("adpasexe", 0)} disabled={disabled} data-testid="padv-sexe-f" /> F
                    </label>
                    <label style={{ fontSize: 12, marginLeft: 6 }}>
                      <input type="radio" name="padv-sexe" checked={form.adpasexe === 1} onChange={() => setField("adpasexe", 1)} disabled={disabled} data-testid="padv-sexe-h" /> M
                    </label>
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>No client (auto)</label>
                  <input className="win-input" style={{ fontSize: 12, padding: '3px 6px', background: "#e0e0e0" }} value={form.adpanoclient} readOnly tabIndex={-1} data-testid="padv-noclient" />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>No dossier de la partie</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpanodoss} onChange={e => setField("adpanodoss", e.target.value)} maxLength={20} disabled={disabled} data-testid="padv-nodoss" />
                </div>
              </div>

              <div style={{ marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Adresse</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpaadr} onChange={e => setField("adpaadr", e.target.value)} maxLength={40} disabled={disabled} data-testid="padv-adr" />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 55px", gap: 6, marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Ville</label>
                  <VilleAutocomplete style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpaville} onChange={v => setField("adpaville", formatNomPropre(v))} maxLength={40} disabled={disabled} testId="padv-ville" />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Code postal</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpacodpos} onChange={e => setField("adpacodpos", formatCodePostal(e.target.value))} maxLength={7} placeholder="A1A 1A1" disabled={disabled} data-testid="padv-cp" />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Prov.</label>
                  <select className="win-select" style={{ fontSize: 13, padding: '3px 4px', minWidth: 'unset' }} value={form.adpaprov} onChange={e => setField("adpaprov", e.target.value)} disabled={disabled} data-testid="padv-prov">
                    {provOptions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Tél. résidence</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpatel_1} onChange={e => setField("adpatel_1", formatTelephone(e.target.value))} maxLength={14} disabled={disabled} data-testid="padv-tel1" />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Tél. autre</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpatel_2} onChange={e => setField("adpatel_2", formatTelephone(e.target.value))} maxLength={14} disabled={disabled} data-testid="padv-tel2" />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 60px", gap: 6, marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Courriel</label>
                  <input className="win-input" type="email" style={{ fontSize: 13, padding: '3px 6px' }} value={form.courriel_padv} onChange={e => setField("courriel_padv", e.target.value.toLowerCase())} maxLength={100} disabled={disabled} data-testid="padv-courriel" />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Ext.</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.ext_padv} onChange={e => setField("ext_padv", e.target.value)} maxLength={10} disabled={disabled} data-testid="padv-ext" />
                </div>
              </div>
            </fieldset>

            {/* ---- Colonne droite : AVOCAT ADVERSE ---- */}
            <fieldset className="win-groupbox compact" style={{ margin: 0, padding: 8 }}>
              <legend style={{ fontSize: 11, padding: '3px 10px' }}>AVOCAT DE LA PARTIE ADVERSE</legend>

              <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Code</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpavocode} onChange={e => setField("adpavocode", e.target.value.toUpperCase())} onBlur={e => handleAvocatCodeChange(e.target.value)} maxLength={6} disabled={disabled} data-testid="padv-avocode" />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Nom (recherche par code ou nom)</label>
                  <AvocatAutocomplete
                    liste={avocats}
                    code={form.adpavocode || ""}
                    nom={[form.adpavonom, form.adpavopren].filter(Boolean).join(", ")}
                    onSelect={applyAvocatSelection}
                    testId="padv-avocat-autocomplete"
                    disabled={disabled}
                    placeholder="Rechercher par code ou nom…"
                  />
                </div>
              </div>

              <div style={{ marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Prénom</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpavopren} onChange={e => setField("adpavopren", e.target.value)} onBlur={e => setField("adpavopren", formatNomPropre(e.target.value))} maxLength={40} disabled={disabled} data-testid="padv-avopren" />
                </div>
              </div>

              <div style={{ marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Adresse</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpavoadr} onChange={e => setField("adpavoadr", e.target.value)} maxLength={60} disabled={disabled} data-testid="padv-avoadr" />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 55px", gap: 6, marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Ville</label>
                  <VilleAutocomplete style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpavoville} onChange={v => setField("adpavoville", formatNomPropre(v))} maxLength={60} disabled={disabled} testId="padv-avoville" />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Code postal</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpavocp} onChange={e => setField("adpavocp", formatCodePostal(e.target.value))} maxLength={7} placeholder="A1A 1A1" disabled={disabled} data-testid="padv-avocp" />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Prov.</label>
                  <select className="win-select" style={{ fontSize: 13, padding: '3px 4px', minWidth: 'unset' }} value={form.adpavoprov} onChange={e => setField("adpavoprov", e.target.value)} disabled={disabled} data-testid="padv-avoprov">
                    {provOptions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 60px", gap: 6, marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Téléphone</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.adpavotel} onChange={e => setField("adpavotel", formatTelephone(e.target.value))} maxLength={14} disabled={disabled} data-testid="padv-avotel" />
                </div>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Ext.</label>
                  <input className="win-input" style={{ fontSize: 13, padding: '3px 6px' }} value={form.ext_avo} onChange={e => setField("ext_avo", e.target.value)} maxLength={10} disabled={disabled} data-testid="padv-avoext" />
                </div>
              </div>

              <div style={{ marginTop: 6 }}>
                <div className="win-field">
                  <label className="win-field-label" style={{ fontSize: 10 }}>Courriel</label>
                  <input className="win-input" type="email" style={{ fontSize: 13, padding: '3px 6px' }} value={form.courriel_avo} onChange={e => setField("courriel_avo", e.target.value.toLowerCase())} maxLength={100} disabled={disabled} data-testid="padv-avocourriel" />
                </div>
              </div>
            </fieldset>
          </div>

          {/* ============ LISTE TABULAIRE (bas) — style LiensTab ============ */}
          <div style={{ marginTop: 10 }}>
            <div className="liens-table-container" style={{ maxHeight: 180, overflowY: "auto", border: "1px inset #808080", background: "#fff" }}>
              {liste.length === 0 ? (
                <p style={{ padding: 16, textAlign: "center", color: "#666", fontStyle: "italic", margin: 0, fontSize: 12 }}>
                  Aucune partie adverse enregistrée pour ce dossier.
                </p>
              ) : (
                <table className="win-table" style={{ width: "100%", fontSize: 12 }} data-testid="padv-list">
                  <thead>
                    <tr>
                      <th style={{ width: 110 }}>Lien</th>
                      <th>Nom</th>
                      <th>Prénom</th>
                      <th style={{ width: 90 }}>Date naiss.</th>
                      <th>Ville</th>
                      <th style={{ width: 130 }}>Avocat adverse</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liste.map(p => {
                      const isSelected = selectedRowId === p.RowId;
                      const avoLabel = [p.adpavopren, p.adpavonom].filter(Boolean).join(" ").trim();
                      const dtn = (p.adpadatenais || "").substring(0, 10);
                      return (
                        <tr
                          key={p.RowId}
                          data-testid={`padv-row-${p.RowId}`}
                          onClick={() => handleSelectRow(p)}
                          style={{
                            cursor: "pointer",
                            background: isSelected ? "#e0edff" : undefined,
                          }}
                        >
                          <td>{(p.adpalien || "").trim() || "-"}</td>
                          <td>{(p.adpanom || "").trim()}</td>
                          <td>{(p.adpaprenom || "").trim()}</td>
                          <td>{dtn || "-"}</td>
                          <td>{(p.adpaville || "").trim() || "-"}</td>
                          <td>{avoLabel || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
              <span style={{ fontSize: 11, color: "#555" }}>
                Cliquez une ligne pour la consulter/modifier
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" className="win-btn" disabled={!pkDaj} title="Imprimer le rapport « Partie adverse »" data-testid="btn-padv-print" onClick={() => { if (pkDaj) openReport({ url: `${API}/daj/${encodeURIComponent(pkDaj)}/parties-adverses-pdf`, title: 'Partie adverse', fileName: buildReportName('PARTIEADVERSE', pkDaj, requerantPrenom, requerantNom) }); }}>Imprimer</button>
                <button type="button" className="win-btn" onClick={handleCloseModal} data-testid="btn-padv-close">Fermer</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartieAdverseModal;
